/**
 * ConfirmDialog Component (enhanced in Task 10.4 and 10.5)
 *
 * Reusable confirmation dialog with Radix Dialog
 * Used for destructive actions requiring user confirmation
 * Includes Framer Motion animations and focus trap
 */

import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { FocusTrap } from '../common/FocusTrap';

interface ConfirmDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;

  /**
   * Callback when dialog should close
   */
  onOpenChange: (open: boolean) => void;

  /**
   * Dialog title
   */
  title: string;

  /**
   * Dialog description/message
   */
  description: string;

  /**
   * Callback when user confirms
   */
  onConfirm: () => void;

  /**
   * Callback when user cancels (optional)
   */
  onCancel?: () => void;

  /**
   * Confirm button text
   * @default "Confirm"
   */
  confirmText?: string;

  /**
   * Cancel button text
   * @default "Cancel"
   */
  cancelText?: string;

  /**
   * Whether the action is destructive (red confirm button)
   * @default true
   */
  destructive?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  destructive = true,
}: ConfirmDialogProps) {
  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            {/* Backdrop with fade animation (Task 10.4) */}
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 bg-black/70"
                style={{ zIndex: 'var(--z-index-modal-backdrop)' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              />
            </Dialog.Overlay>

            {/* Dialog Content with scale + fade animation (Task 10.4) */}
            <Dialog.Content asChild>
              <motion.div
                className="fixed left-1/2 top-1/2 max-w-md rounded-lg border p-6"
                style={{
                  backgroundColor: 'var(--color-dark-panel)',
                  borderColor: 'var(--color-dark-border)',
                  boxShadow: 'var(--shadow-lg)',
                  zIndex: 'var(--z-index-modal)',
                  transform: 'translate(-50%, -50%)',
                }}
                initial={{ opacity: 0, scale: 0.95, y: '-50%', x: '-50%' }}
                animate={{ opacity: 1, scale: 1, y: '-50%', x: '-50%' }}
                exit={{ opacity: 0, scale: 0.95, y: '-50%', x: '-50%' }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <FocusTrap active={open} initialFocus="button[data-confirm]">
                  {/* Close Button */}
                  <Dialog.Close asChild>
                    <button
                      className="absolute right-4 top-4 rounded-md p-1 text-neutral-400 transition-colors hover:bg-neutral-700 hover:text-neutral-200"
                      aria-label="Close"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </Dialog.Close>

                  {/* Title */}
                  <Dialog.Title
                    className="mb-2 text-lg font-semibold"
                    style={{ color: 'var(--color-dark-text)' }}
                  >
                    {title}
                  </Dialog.Title>

                  {/* Description */}
                  <Dialog.Description
                    className="mb-6 text-sm"
                    style={{ color: 'var(--color-dark-text-muted)' }}
                  >
                    {description}
                  </Dialog.Description>

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3">
                    <motion.button
                      onClick={handleCancel}
                      className="rounded px-4 py-2 text-sm font-medium transition-colors"
                      style={{
                        backgroundColor: 'transparent',
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        borderColor: 'var(--color-dark-border)',
                        color: 'var(--color-dark-text)',
                      }}
                      whileHover={{
                        scale: 1.02,
                        backgroundColor: 'var(--color-dark-bg)',
                      }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {cancelText}
                    </motion.button>

                    <motion.button
                      onClick={handleConfirm}
                      data-confirm
                      className="rounded px-4 py-2 text-sm font-medium text-white transition-colors"
                      style={{
                        backgroundColor: destructive
                          ? 'var(--color-body-control)'
                          : 'var(--color-body-head)',
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {confirmText}
                    </motion.button>
                  </div>
                </FocusTrap>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
